/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/pro-solid-svg-icons";
import capitalize from "../../../helpers/capitalize";
import { Reorder, AnimatePresence } from "framer-motion";
import ImageUpload from "../../../components/admin/ImageUpload";
import { adminApi } from "../../../api";
import inputFields from "./inputFields";
import LoadingSpinner from "../../../components/loading/LoadingSpinner";
import Note from "../../../interfaces/mortgageNote";
import addCommas from "../../../helpers/addCommas";

export interface CreateNoteParams {
  loanNumber: string;
  isPerforming: boolean;
  status: string;
  loanType: string;
  amountHeld: string;
  lienPosition: string;
  loanAmount: string;
  purchasePrice: string;
  marketValue: string;
  downPayment: string;
  ltv: string;
  interestRate: string;
  loanTerm: string;
  useOfProceeds: string;
  exitStrategy: string;
  maturityDate: string;
  // salePrice?: string;
  averageCredit: string;
  averageBankBalance: string;
  repeatBorrower: boolean;
  paidOffPreviously: boolean;
  loanPackagePdf: FileInput;
  propertyDetailsExcel: FileInput;
  propertyType: string;
  streetNumber: string;
  city: string;
  state: string;
  postalCode: string;
  numberOfBedrooms: string;
  numberOfBathrooms: string;
  squareFeet: string;
  highlights: string[];
  propertyImages: PropertyImage[];
  shouldSendNewsletterEmail: boolean;
}

interface FileInput {
  file: File;
  fileName: string;
}

interface PropertyImage {
  file: File;
  alt: string;
  fileName: string;
  order_position: number;
  tag_text?: string;
  tag_color?: string;
}

const NoteEditNew = () => {
  const { noteID } = useParams();

  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [formData, setFormData] = useState<CreateNoteParams>(() => {
    const initialData: CreateNoteParams = {
      loanNumber: "",
      isPerforming: true,
      status: "",
      loanType: "",
      amountHeld: "",
      lienPosition: "",
      loanAmount: "",
      purchasePrice: "",
      marketValue: "",
      downPayment: "",
      ltv: "",
      interestRate: "",
      loanTerm: "",
      useOfProceeds: "",
      exitStrategy: "",
      maturityDate: "",
      averageCredit: "",
      averageBankBalance: "",
      repeatBorrower: false,
      paidOffPreviously: false,
      loanPackagePdf: { file: new File([], ""), fileName: "" },
      propertyDetailsExcel: { file: new File([], ""), fileName: "" },
      propertyType: "",
      streetNumber: "",
      city: "",
      state: "",
      postalCode: "",
      numberOfBedrooms: "",
      numberOfBathrooms: "",
      squareFeet: "",
      highlights: [],
      propertyImages: [] as PropertyImage[],
      shouldSendNewsletterEmail: false,
    };

    // Initialize select inputs with their first option
    inputFields.forEach((field) => {
      if (
        field.type === "select" &&
        field.options &&
        field.options.length > 0
      ) {
        const firstOption = field.options[0];
        (initialData[field.name as keyof CreateNoteParams] as any) =
          typeof firstOption === "string"
            ? firstOption
            : (firstOption as { value: string }).value;
      }
    });

    return initialData;
  });
  const [newHighlight, setNewHighlight] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Fetch data if editing an existing note
    if (noteID) {
      // Make an API call to fetch note data by ID and populate formData
      fetchNoteById(noteID);
    }
  }, [noteID]);

  interface FormErrors {
    [key: string]: string;
  }

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  const fetchNoteById = async (id: string) => {
    // Mock fetch function for example purposes
    // Replace this with actual API call
    const fetchedData = {}; // Replace with actual fetched data
    setFormData({ ...formData, ...fetchedData });
  };

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    if (type === "file") {
      const fileInput = e.target as HTMLInputElement;
      const file = fileInput.files ? fileInput.files[0] : null;
      setFormData((prevData) => ({
        ...prevData,
        [name]: {
          file,
          fileName: file?.name || "",
        },
      }));
      return;
    }

    const fieldConfig = inputFields.find((field) => field.name === name);
    let newValue = value;

    // Remove commas from input if it's a currency field
    if (fieldConfig?.displayType === "currency") {
      newValue = value.replace(/,/g, "");
    }

    // Basic input restrictions
    if (fieldConfig?.inputRestriction === "number") {
      newValue = newValue.replace(/\D/g, "");
      if (name === "averageCredit") {
        newValue = newValue.slice(0, 3);
      }
    } else if (fieldConfig?.inputRestriction === "decimal") {
      // Allow decimal point and numbers only
      newValue = newValue.replace(/[^\d.]/g, "");

      // Handle decimal places
      const parts = newValue.split(".");
      if (parts[1]) {
        parts[1] = parts[1].slice(0, 2); // Limit to 2 decimal places
        newValue = parts.join(".");
      }

      // Ensure only one decimal point
      if ((newValue.match(/\./g) || []).length > 1) {
        newValue = newValue.replace(/\./g, (match, index) =>
          index === newValue.indexOf(".") ? "." : ""
        );
      }
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : newValue,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: FormErrors = {};

    inputFields.forEach((field) => {
      const value = (formData as any)[field.name];

      if (field.validations) {
        field.validations.forEach((validation: any) => {
          switch (validation.type) {
            case "required":
              if (!value || value.trim() === "") {
                newErrors[field.name] = validation.message;
              }
              break;
            case "minLength":
              if (value.length < validation.value) {
                newErrors[field.name] = validation.message;
              }
              break;
            case "maxLength":
              if (value.length > validation.value) {
                newErrors[field.name] = validation.message;
              }
              break;
            case "minValue":
              if (parseFloat(value.replace(/,/g, "")) < validation.value) {
                newErrors[field.name] = validation.message;
              }
              break;
            case "maxValue":
              if (parseFloat(value.replace(/,/g, "")) > validation.value) {
                newErrors[field.name] = validation.message;
              }
              break;
            case "range":
              const numValue = parseFloat(value);
              if (numValue < validation.min || numValue > validation.max) {
                newErrors[field.name] = validation.message;
              }
              break;
          }
        });
      }
    });

    setFormErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      try {
        setIsLoading(true);
        const note: Note = await adminApi.createNote(formData);
        navigate(`/notes/${note.id}`);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoading(false);
      }
    }
  };

  const handleAddHighlight = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    if (newHighlight.trim()) {
      setFormData((prev) => ({
        ...prev,
        highlights: [...prev.highlights, newHighlight.trim()],
      }));
      setNewHighlight("");
    }
  };

  const handleRemoveHighlight = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      highlights: prev.highlights.filter((_, i) => i !== index),
    }));
  };

  const handleAddImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newImages = Array.from(e.target.files).map((file) => ({
        file,
        alt: "",
        fileName: file?.name || "",
        order_position: formData.propertyImages.length,
        tag_text: "",
        tag_color: "#000000",
      }));

      setFormData((prev) => ({
        ...prev,
        propertyImages: [...prev.propertyImages, ...newImages],
      }));
    }
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      propertyImages: prev.propertyImages.filter((_, i) => i !== index),
    }));
  };

  const handleAltChange = (index: number, alt: string) => {
    setFormData((prev) => ({
      ...prev,
      propertyImages: prev.propertyImages.map((img, i) =>
        i === index ? { ...img, alt } : img
      ),
    }));
  };

  const handleTagChange = (
    index: number,
    tag_text: string,
    tag_color: string
  ) => {
    setFormData((prev) => ({
      ...prev,
      propertyImages: prev.propertyImages.map((img, i) =>
        i === index ? { ...img, tag_text, tag_color } : img
      ),
    }));
  };

  const handleReorderImages = (newOrder: PropertyImage[]) => {
    setFormData((prev) => ({
      ...prev,
      propertyImages: newOrder,
    }));
  };

  const triggerFileInput = () => {
    fileInputRef.current?.click();
  };

  const getInputClassName = (fieldName: string, baseClass: string) => {
    return `${baseClass} ${
      formErrors[fieldName] ? "border-red-500" : ""
    }`.trim();
  };

  // Add this new function to format display values
  const formatDisplayValue = (value: string, type: string) => {
    if (!value) return "";

    if (type === "currency") {
      // If user is typing a decimal point, preserve it
      if (value.endsWith(".")) {
        const parts = value.slice(0, -1).replace(/,/g, "").split(".");
        const wholeWithCommas = addCommas(Number(parts[0]));
        return `${wholeWithCommas}.`;
      }

      // Remove any existing commas first
      const cleanValue = value.replace(/,/g, "");
      const parts = cleanValue.split(".");

      // Format the whole number part with commas
      const wholeWithCommas = addCommas(Number(parts[0]));

      // Handle decimal part if it exists
      if (parts[1]) {
        return `${wholeWithCommas}.${parts[1].slice(0, 2)}`;
      }

      return wholeWithCommas;
    }

    return value;
  };

  return (
    <div className="w-full flex items-center justify-center flex-col columns-1">
      <h1 className="text-[40px] py-4">
        {noteID ? "Edit Note" : "Create Note"}
      </h1>
      <form onSubmit={handleSubmit} className="flex flex-col columns-1 gap-3">
        {/* Map over inputFields array */}
        {inputFields.map((field) => {
          // Skip fields that have showWhen condition that evaluates to false
          if (field.showWhen && !field.showWhen(formData)) {
            return null;
          }

          return (
            <div
              key={field.name}
              className={`form-group flex ${
                field.type === "checkbox"
                  ? "flex-row justify-between py-2"
                  : "flex-col"
              } columns-1 gap-1`}
            >
              <label htmlFor={field.name} className="font-bold text-white">
                {field.label}
              </label>
              {field.type === "select" ? (
                <select
                  id={field.name}
                  name={field.name}
                  value={(formData as any)[field.name]}
                  onChange={handleInputChange}
                  className={getInputClassName(
                    field.name,
                    "select select-bordered bg-base-200"
                  )}
                >
                  {field.options?.map((option) => {
                    if (typeof option === "string") {
                      return (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      );
                    } else if (typeof option === "object") {
                      return (
                        <option
                          key={`${option.value}-${option.name}`}
                          value={option.value}
                        >
                          {capitalize(option.name)}
                        </option>
                      );
                    }
                    return null;
                  })}
                </select>
              ) : field.type === "textarea" ? (
                <textarea
                  id={field.name}
                  name={field.name}
                  value={(formData as any)[field.name]}
                  onChange={handleInputChange}
                  className={getInputClassName(
                    field.name,
                    "textarea textarea-bordered bg-base-200"
                  )}
                  data-1p-ignore
                />
              ) : (
                <input
                  type={field.type}
                  id={field.name}
                  name={field.name}
                  value={
                    field.type !== "file" && field.type !== "checkbox"
                      ? field.displayType === "currency"
                        ? formatDisplayValue(
                            (formData as any)[field.name],
                            "currency"
                          )
                        : (formData as any)[field.name]
                      : undefined
                  }
                  checked={
                    field.type === "checkbox"
                      ? (formData as any)[field.name]
                      : undefined
                  }
                  onChange={handleInputChange}
                  className={getInputClassName(
                    field.name,
                    field.type === "checkbox"
                      ? "checkbox"
                      : field.type === "file"
                      ? "file-input bg-base-200 file-input-bordered file-input-info"
                      : "input input-bordered bg-base-200"
                  )}
                  data-1p-ignore
                />
              )}
              {formErrors[field.name] && (
                <span className="text-red-500">{formErrors[field.name]}</span>
              )}
            </div>
          );
        })}

        {/* Highlights section */}
        <div className="form-group flex flex-col columns-1 gap-1">
          <label htmlFor="newHighlight" className="font-bold text-white">
            Add Highlight
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              id="newHighlight"
              value={newHighlight}
              onChange={(e) => setNewHighlight(e.target.value)}
              className="input input-bordered bg-base-200 flex-grow"
              placeholder="Enter a highlight"
            />
          </div>
          <div className="w-full flex justify-end mt-1">
            <button
              className="btn btn-sm btn-info max-w-12"
              onClick={handleAddHighlight}
              disabled={newHighlight.trim() === ""}
            >
              Add
            </button>
          </div>
        </div>

        {/* Display added highlights */}
        <div className="mt-1">
          <h3 className="font-bold text-white mb-2">Highlights:</h3>
          <div className="flex flex-wrap gap-2 flex-col columns-1 w-full">
            {formData.highlights.map((highlight, index) => (
              <div
                key={index}
                className="bg-base-300 py-1 px-2 rounded-lg relative break-words max-w-[450px]"
              >
                <button
                  className="rounded-lg btn-xs absolute -top-[14px] -right-[16px] bg-gray-500"
                  onClick={() => handleRemoveHighlight(index)}
                >
                  <FontAwesomeIcon icon={faXmark} />
                </button>
                <span className="whitespace-normal pl-1.5">{highlight}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Property Images section */}
        <div className="form-group flex flex-col columns-1 gap-1 mt-4">
          <label className="font-bold text-white">Add Images</label>
          <div className="flex gap-2">
            <input
              type="file"
              ref={fileInputRef}
              onChange={handleAddImage}
              className="hidden"
              multiple
              accept="image/*"
            />
            <div
              className="border-2 border-dashed rounded-lg p-2 border-zinc-500 flex flex-col items-center justify-center py-12 px-4 cursor-pointer"
              onClick={triggerFileInput}
            >
              <div>Drag your image(s) here to begin or click to browse</div>
            </div>
          </div>
        </div>

        {/* Display uploaded images */}
        <div className="mt-4">
          <h3 className="font-bold text-white mb-2">Images:</h3>
          <Reorder.Group
            axis="y"
            values={formData.propertyImages}
            onReorder={handleReorderImages}
            className="space-y-2"
          >
            <AnimatePresence initial={false}>
              {formData.propertyImages.map((image, index) => (
                <Reorder.Item
                  key={image.fileName}
                  value={image}
                  className="cursor-move"
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    opacity: { duration: 0.2 },
                    layout: {
                      type: "spring",
                      bounce: 0.2,
                      duration: 0.6,
                      damping: 25,
                      stiffness: 200,
                    },
                  }}
                  whileDrag={{
                    scale: 1.01,
                    boxShadow: "0 3px 10px rgba(0,0,0,0.2)",
                    cursor: "grabbing",
                  }}
                >
                  <ImageUpload
                    image={image}
                    index={index}
                    onRemove={handleRemoveImage}
                    onAltChange={handleAltChange}
                    onTagChange={handleTagChange}
                  />
                </Reorder.Item>
              ))}
            </AnimatePresence>
          </Reorder.Group>
        </div>
        <div className="form-group flex flex-row justify-between gap-1">
          <label htmlFor="sendNewsletterEmail" className="font-bold text-white">
            Email this note to Newsletter Subscribers
          </label>
          <input
            type="checkbox"
            id="shouldSendNewsletterEmail"
            name="shouldSendNewsletterEmail"
            checked={formData.shouldSendNewsletterEmail}
            onChange={handleInputChange}
            className="checkbox"
          />
        </div>
        <button
          type="submit"
          className="btn btn-info text-white font-bold text-xl mb-4 mt-8"
        >
          {isLoading ? (
            <LoadingSpinner className="w-5 h-5" />
          ) : (
            <span>{noteID ? "Update Note" : "Create Note"}</span>
          )}
        </button>
      </form>
    </div>
  );
};

export default NoteEditNew;
